import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AuthPage() {
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const afterAuth = async () => {    const pending = sessionStorage.getItem("pending_checkout");
    if (pending) {
      sessionStorage.removeItem("pending_checkout");
      try {
        const p = JSON.parse(pending);
        const { data } = await api.post("/payments/checkout", {
          ...p,
          origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
        return;
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
    navigate("/dashboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (tab === "forgot") {
        await api.post("/auth/forgot", { email: form.email });
        setForgotSent(true);
      } else {
        if (tab === "login") await login(form.email, form.password);
        else await register(form.name, form.email, form.password);
        await afterAuth();
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-black/15 bg-white px-5 py-3.5 text-sm text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900";

  return (
    <div data-testid="auth-page" className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#1E293B] text-white p-14">
        <Link to="/" className="font-display font-extrabold text-xl tracking-tight w-max">
          LUMORA<span className="text-[#E05638]">.</span>
        </Link>
        <div>
          <p className="mono-label text-[#E05638] mb-6">Area Clienti</p>
          <h1 className="font-display font-bold tracking-tight leading-[1.08] text-4xl xl:text-5xl">
            Il tuo sito, le tue metriche, i tuoi risultati.
          </h1>
          <p className="mt-6 text-neutral-300 max-w-md leading-relaxed">
            Accedi per monitorare lo stato del sito, i report SEO e il tuo abbonamento in tempo
            reale.
          </p>
        </div>
        <p className="mono-label text-neutral-500">© 2026 Lumora</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <Link
            to="/"
            data-testid="auth-back-home"
            className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-[#111111] transition-colors mb-10"
          >
            <ArrowLeft size={15} /> Torna alla home
          </Link>

          <div className="flex gap-2 p-1 bg-[#F3F0EA] rounded-full w-max mb-8">
            <button
              data-testid="client-login-tab"
              onClick={() => setTab("login")}
              className={`rounded-full px-6 py-2 text-sm font-medium transition-colors duration-300 ${
                tab === "login" ? "bg-[#111111] text-white" : "text-neutral-600 hover:text-[#111111]"
              }`}
            >
              Accedi
            </button>
            <button
              data-testid="client-register-tab"
              onClick={() => setTab("register")}
              className={`rounded-full px-6 py-2 text-sm font-medium transition-colors duration-300 ${
                tab === "register" ? "bg-[#111111] text-white" : "text-neutral-600 hover:text-[#111111]"
              }`}
            >
              Registrati
            </button>
          </div>

          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">
            {tab === "login" ? "Bentornato." : tab === "register" ? "Crea il tuo account." : "Recupera password."}
          </h2>
          <p className="text-neutral-500 text-sm mt-2 mb-8">
            {tab === "login"
              ? "Accedi alla tua area clienti."
              : tab === "register"
              ? "Registrati per attivare il tuo piano e accedere alla dashboard."
              : "Inserisci la tua email: ti inviamo un link per reimpostare la password."}
          </p>

          {forgotSent ? (
            <div data-testid="forgot-success" className="rounded-xl bg-emerald-500/10 text-emerald-700 text-sm px-5 py-4 leading-relaxed">
              Email inviata! Controlla la tua casella (anche spam) e clicca il link per scegliere la nuova password.
              <button
                data-testid="forgot-back-login"
                onClick={() => {
                  setTab("login");
                  setForgotSent(false);
                }}
                className="block mt-3 font-semibold text-[#111111] hover:text-[#E05638] transition-colors"
              >
                ← Torna al login
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={submit} className="space-y-4">
                {tab === "register" && (
                  <input
                    data-testid="auth-name-input"
                    required
                    placeholder="Nome e cognome / Azienda"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                  />
                )}
                <input
                  data-testid="auth-email-input"
                  required
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                />
                {tab !== "forgot" && (
                  <>
                    <input
                      data-testid="auth-password-input"
                      required
                      type="password"
                      minLength={8}
                      placeholder="Password (min. 8 caratteri)"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className={inputCls}
                    />
                    {tab === "login" && (
                      <div className="text-right">
                        <button
                          type="button"
                          data-testid="forgot-password-link"
                          onClick={() => setTab("forgot")}
                          className="text-xs text-neutral-500 hover:text-[#E05638] transition-colors"
                        >
                          Password dimenticata?
                        </button>
                      </div>
                    )}
                  </>
                )}
                {error && (
                  <p data-testid="auth-error-message" className="text-sm text-[#C8452A] bg-[#E05638]/10 rounded-lg px-4 py-3">
                    {error}
                  </p>
                )}
                <button
                  data-testid="auth-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#111111] text-white py-3.5 text-sm font-semibold hover:bg-[#E05638] transition-colors duration-300 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {tab === "login" ? "Accedi" : tab === "register" ? "Crea account" : "Invia link di recupero"}
                </button>
              </form>

              {tab === "forgot" && (
                <button
                  type="button"
                  data-testid="forgot-cancel-button"
                  onClick={() => setTab("login")}
                  className="mt-4 text-xs text-neutral-500 hover:text-[#111111] transition-colors"
                >
                  ← Torna al login
                </button>
              )}

              {tab !== "forgot" && (
                <>
                  <div className="flex items-center gap-4 mt-6 mb-6">
                    <span className="flex-1 h-px bg-black/10" />
                    <span className="mono-label text-neutral-400">oppure</span>
                    <span className="flex-1 h-px bg-black/10" />
                  </div>

                  <button
                    data-testid="google-login-button"
                    onClick={googleLogin}
                    className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-white border border-black/15 py-3.5 text-sm font-semibold text-neutral-800 hover:border-[#111111] hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2"
                  >
                    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
                      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/>
                    </svg>
                    Continua con Google
                  </button>
                </>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
